import React, { useState } from 'react';
import { Container, Row, Col, Accordion, Button } from 'react-bootstrap';
import { PageLayout2 } from '../../Components/PageLayout/PageLayout2';
import './APIDocumentationPage.scss';

export const APIDocumentationContent = () => {
    const [copied, setCopied] = useState(false);

    const handleCopy = (code) => {
        navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Container fluid className='h-100 overflow-y-auto'>
            <Row className='h-100'>
                <Col md={12} className='flex-grow-1 py-2'>
                    <div className='api-doc-container'>
                        <h1>API Documentation - Data Extraction</h1>
                        <p>This API allows developers to extract data using specific services, referred to as "Pipelines." The API requires authentication via Azure EntraID.</p>

                        <h2>Authentication</h2>
                        <p>All endpoints require authentication. Users need to log in using their Microsoft credentials, either a Personal account, Work, or School account. The API handles the necessary client ID and tenant ID internally. However, users must be registered in the application database by a system administrator before they can access the API.</p>

                        <h3>Login Example:</h3>
                        <pre className='code-block'>
                            <code>
                                {`
// Example (JavaScript)
const login = async () => {
  const response = await fetch("/api/auth/login", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      username: "user@example.com",
      password: "your_password_here"
    })
  });

  const data = await response.json();
  const token = data.accessToken;

  // Store the token for future API requests
  localStorage.setItem('accessToken', token);
};

login();
                                `}
                            </code>
                            <Button size="sm" variant="outline-primary" onClick={() => handleCopy(`
// Example (JavaScript)
const login = async () => {
  const response = await fetch("/api/auth/login", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      username: "user@example.com",
      password: "your_password_here"
    })
  });

  const data = await response.json();
  const token = data.accessToken;

  // Store the token for future API requests
  localStorage.setItem('accessToken', token);
};

login();
                                `)} className="copy-btn">
                                {copied ? "Copied!" : "Copy"}
                            </Button>
                        </pre>

                        <h3>Refresh Token Example:</h3>
                        <pre className='code-block'>
                            <code>
                                {`
// Example (JavaScript)
const refreshToken = async () => {
  const token = localStorage.getItem('accessToken');

  const response = await fetch("/api/auth/refresh", {
    method: "POST",
    headers: {
      "Authorization": \`Bearer \${token}\`,
      "Content-Type": "application/json"
    }
  });

  const data = await response.json();
  const newToken = data.accessToken;

  // Update the stored token
  localStorage.setItem('accessToken', newToken);
};

refreshToken();
                                `}
                            </code>
                            <Button size="sm" variant="outline-primary" onClick={() => handleCopy(`
// Example (JavaScript)
const refreshToken = async () => {
  const token = localStorage.getItem('accessToken');

  const response = await fetch("/api/auth/refresh", {
    method: "POST",
    headers: {
      "Authorization": \`Bearer \${token}\`,
      "Content-Type": "application/json"
    }
  });

  const data = await response.json();
  const newToken = data.accessToken;

  // Update the stored token
  localStorage.setItem('accessToken', newToken);
};

refreshToken();
                                `)} className="copy-btn">
                                {copied ? "Copied!" : "Copy"}
                            </Button>
                        </pre>

                        <h2>Endpoints</h2>
                        <Accordion defaultActiveKey="0">
                            <Accordion.Item eventKey="0">
                                <Accordion.Header>POST /ExtractData</Accordion.Header>
                                <Accordion.Body>
                                    <p>Description: Extract data from the database using a specific service (Pipeline).</p>
                                    <p><strong>Request Body:</strong></p>
                                    <pre className='code-block'>
                                        <code>
                                            {`{
  "PipelineId": 1,
  "Parameters": [
    {"Name": "ParameterName", "Value": "ParameterValue"}
  ],
  "PageNumber": 1,
  "PageSize": 50
}`}
                                        </code>
                                        <Button size="sm" variant="outline-primary" onClick={() => handleCopy(`{
  "PipelineId": 1,
  "Parameters": [
    {"Name": "ParameterName", "Value": "ParameterValue"}
  ],
  "PageNumber": 1,
  "PageSize": 50
}`)} className="copy-btn">
                                            {copied ? "Copied!" : "Copy"}
                                        </Button>
                                    </pre>
                                    <p><strong>Expected Response:</strong></p>
                                    <pre className='code-block'>
                                        <code>
                                            {`{
  "Data": [],
  "PageNumber": 1,
  "PageSize": 50,
  "TotalRecords": 100
}`}
                                        </code>
                                        <Button size="sm" variant="outline-primary" onClick={() => handleCopy(`{
  "Data": [],
  "PageNumber": 1,
  "PageSize": 50,
  "TotalRecords": 100
}`)} className="copy-btn">
                                            {copied ? "Copied!" : "Copy"}
                                        </Button>
                                    </pre>
                                    <p><strong>Error Handling:</strong></p>
                                    <pre className='code-block'>
                                        <code>
                                            {`{
  "Error": "Invalid Pipeline ID"
}`}
                                        </code>
                                        <Button size="sm" variant="outline-primary" onClick={() => handleCopy(`{
  "Error": "Invalid Pipeline ID"
}`)} className="copy-btn">
                                            {copied ? "Copied!" : "Copy"}
                                        </Button>
                                    </pre>
                                </Accordion.Body>
                            </Accordion.Item>

                            <Accordion.Item eventKey="1">
                                <Accordion.Header>GET /GetAvailableServices</Accordion.Header>
                                <Accordion.Body>
                                    <p>Description: Retrieve a list of available services (Pipelines).</p>
                                    {/* Add more details similar to the above */}
                                </Accordion.Body>
                            </Accordion.Item>
                            {/* Add more Accordion.Items for other endpoints */}
                        </Accordion>

                        <h2>Services (Pipelines)</h2>
                        <p>Explanation of how services and parameter groups are used in data extraction.</p>
                        {/* Add details and examples */}

                        <h2>FAQ</h2>
                        <p>Common questions and issues related to using the API.</p>
                        {/* Add FAQ content */}
                    </div>
                </Col>
            </Row>
        </Container>
    );
};

export const APIDocumentationPage = () => {
    return (
        <PageLayout2 title="Data Extraction API Docs">
            <APIDocumentationContent />
        </PageLayout2>
    );
};

export default APIDocumentationPage;
